// @flow
import React, { Fragment } from 'react';

import Link from 'app/common/components/Link';
import help from 'app/developer-v3/components/PartnerSupportForm/components/blocks/Help';
import radio from 'app/developer-v3/components/PartnerSupportForm/components/blocks/Radio';
import { BASE_DOC_URL_VB } from 'app/developer-v3/constants';
import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import {
  getUserFields,
  messageField,
} from 'app/developer-v3/components/PartnerSupportForm/content/nodes/common';

export const createZapTemplates = (isLoggedIn: boolean) => ({
  value: 'zap-templates',
  label: 'Zap templates',
  zendeskCustomFieldValue: 'pcf-existing-templates',
  child: radio({
    id: 'zaptemplates',
    label: "Let's get you some help",
    zendeskCustomFieldId: 360044582632,
    items: [
      {
        value: 'what-are-zap-templates',
        label: 'What are Zap templates?',
        zendeskCustomFieldValue: 'pcf-existing-template-what',
        child: help({
          docs: {
            title: 'Zap templates',
            body: () => (
              <Fragment>
                <p>
                  Zap templates are readymade integrations or Zaps with the apps
                  and core fields pre-selected, for publicly available Zapier
                  integrations. In a few clicks, they help people discover a use
                  case, connect apps, and turn on the Zap. Zap templates are the
                  fastest way for your users to automate workflows. (Zap
                  Templates are available only to public partners.)
                </p>
              </Fragment>
            ),
            links: [
              {
                href: `${BASE_DOC_URL_VB}/publish/zap-templates`,
                title: 'Zap templates guide',
              },
            ],
          },
          contact: [
            ...getUserFields(isLoggedIn),
            {
              ...messageField,
              details: `Please let us know what questions you have about Zap templates.`,
            },
          ],
        }),
      },
      {
        value: 'creating-zap-templates',
        label: 'Creating and editing Zap templates',
        zendeskCustomFieldValue: 'pcf-existing-template-creating-editing',
        child: help({
          docs: {
            title: 'Creating and editing Zap templates',
            body: () => (
              <Fragment>
                <p>
                  Zap templates can be created and managed from your dashboard{' '}
                  <Link
                    href={`${ZAPIER_APP_BASE_URL}/developer/zap-templates/`}
                    target="_blank"
                  >
                    here
                  </Link>
                  .
                </p>
                <p>
                  {' '}
                  Their key components are a simple and useful workflow,
                  pre-mapped fields to speed up user setup, a clear informative
                  title, and an engaging description.
                </p>
                <p>
                  Any Partner can create Zap templates and submit them for our
                  review so they can be published and available for embedding.
                  Templates can be edited while in Draft modes, or if they have
                  been Rejected by our editorial team. Published templates can
                  not be edited by partners - if you have edits to request,
                  please write in and indicate the ID of the templates in
                  question.
                </p>
              </Fragment>
            ),
            links: [
              {
                href: `${BASE_DOC_URL_VB}/publish/zap-templates#how-to-build-a-zap-template`,
                title: 'How to build a Zap template',
              },
            ],
          },
          contact: [
            ...getUserFields(isLoggedIn),
            {
              ...messageField,
              details: `Please let us know what questions you have about Zap templates.`,
            },
          ],
        }),
      },
      {
        value: 'zap-template-reviews',
        label: 'Zap template reviews',
        zendeskCustomFieldValue: 'pcf-existing-template-review',
        child: help({
          docs: {
            title: 'Zap template reviews',
            body: () => (
              <Fragment>
                <p>
                  To be featured on our{' '}
                  <Link href={`${ZAPIER_APP_BASE_URL}/apps`} target="_blank">
                    App Directory
                  </Link>{' '}
                  and available in our embedding tools, Zap templates must be
                  submitted for review after creation. Allow for up to 14 days
                  for reviews of Zap templates batches - you will receive
                  automatic emails whenever your templates are published or
                  rejected.
                </p>
                <p>
                  Rejected Zap templates can be{' '}
                  <Link
                    href={`${ZAPIER_APP_BASE_URL}/zap-templates/`}
                    target="_blank"
                  >
                    edited in your dashboard
                  </Link>{' '}
                  and re-submitted for review
                </p>
              </Fragment>
            ),
            links: [
              {
                href: `${BASE_DOC_URL_VB}/publish/zap-templates#5-submit-zap-template-for-review`,
                title: 'Submit Zap template for review',
              },
            ],
          },
          contact: [
            ...getUserFields(isLoggedIn),
            {
              ...messageField,
              details: `Please let us know what questions you have about Zap template reviews.`,
            },
          ],
        }),
      },
      {
        value: 'zap-template-errors',
        label: 'Errors',
        zendeskCustomFieldValue: 'pcf-existing-template-errors',
        child: help({
          docs: {
            title: 'Errors',
            body: () => (
              <Fragment>
                <p>
                  Common issues with Zap templates can be found in the page
                  below.
                </p>
              </Fragment>
            ),
            links: [
              {
                href: `${BASE_DOC_URL_VB}/publish/zap-templates`,
                title: 'Troubleshooting Zap templates',
              },
            ],
          },
          contact: [
            ...getUserFields(isLoggedIn),
            {
              ...messageField,
              details: `Please let us know what kind of Errors you're getting.`,
            },
          ],
        }),
      },
    ],
  }),
});
